import { ApiService } from './api';

// MFA-related interfaces
export interface MFAEnrollResponse {
  secret: string;
  qr_code_svg: string;
  backup_codes: string[];
  issuer: string;
  account: string;
}

export interface MFAVerifyEnrollmentRequest {
  code: string;
}

export interface MFAVerifyEnrollmentResponse {
  success: boolean;
  message: string;
  backup_codes: string[];
}

export interface MFAVerifyRequest {
  email: string;
  code: string;
  use_backup_code?: boolean;
}

export interface MFAVerifyResponse {
  token: string;
  token_type: string;
  expires_in: number;
  user: any;
  session_id: string;
}

export interface MFADisableRequest {
  password: string;
}

export interface MFAStatusResponse {
  mfa_enabled: boolean;
  enrolled_at?: string;
  backup_codes_remaining?: number;
}

export interface MFARegenerateBackupCodesRequest {
  password: string;
}

export interface MFARegenerateBackupCodesResponse {
  backup_codes: string[];
}

/**
 * Multi-Factor Authentication Service
 * Handles all MFA-related API calls
 */
export class MfaService {
  /**
   * Enroll in MFA - Get QR code and secret
   */
  static async enroll(): Promise<MFAEnrollResponse> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: MFAEnrollResponse;
      }>('/mfa/enroll');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to enroll in MFA');
      }
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'MFA already enabled');
      }
      throw new Error(error.message || 'Failed to enroll in MFA');
    }
  }

  /**
   * Verify enrollment code and complete MFA setup
   */
  static async verifyEnrollment(request: MFAVerifyEnrollmentRequest): Promise<MFAVerifyEnrollmentResponse> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: {
          backup_codes: string[];
        };
      }>('/mfa/verify-enrollment', request);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to verify enrollment code');
      }
      
      return {
        success: true,
        message: response.message,
        backup_codes: response.data.backup_codes
      };
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid verification code');
      }
      if (error.response?.status === 422) {
        throw new Error('Invalid verification code format');
      }
      throw new Error(error.message || 'Failed to verify enrollment code');
    }
  }

  /**
   * Verify MFA code during login
   */
  static async verify(request: MFAVerifyRequest): Promise<MFAVerifyResponse> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: MFAVerifyResponse;
      }>('/mfa/verify', request);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to verify MFA code');
      }
      
      // Store authentication data
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user_data', JSON.stringify(response.data.user));
      localStorage.setItem('session_id', response.data.session_id);
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid MFA code');
      }
      if (error.response?.status === 404) {
        throw new Error('MFA not enabled for this account');
      }
      if (error.response?.status === 422) {
        throw new Error('Invalid code format');
      }
      if (error.response?.status === 429) {
        throw new Error('Too many failed attempts. Please try again later.');
      }
      throw new Error(error.message || 'Failed to verify MFA code');
    }
  }

  /**
   * Disable MFA for current user
   */
  static async disable(request: MFADisableRequest): Promise<void> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
      }>('/mfa/disable', request);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to disable MFA');
      }
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'MFA is not enabled');
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid password');
      }
      throw new Error(error.message || 'Failed to disable MFA');
    }
  }

  /**
   * Get MFA status for current user
   */
  static async getStatus(): Promise<MFAStatusResponse> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        message: string;
        data: MFAStatusResponse;
      }>('/mfa/status');
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to get MFA status');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to get MFA status');
    }
  }

  /**
   * Regenerate backup codes
   */
  static async regenerateBackupCodes(request: MFARegenerateBackupCodesRequest): Promise<MFARegenerateBackupCodesResponse> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        message: string;
        data: {
          backup_codes: string[];
        };
      }>('/mfa/regenerate-backup-codes', request);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to regenerate backup codes');
      }
      
      return {
        backup_codes: response.data.backup_codes
      };
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'MFA is not enabled');
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid password');
      }
      throw new Error(error.message || 'Failed to regenerate backup codes');
    }
  }

  /**
   * Helper: Check if MFA is enabled for user
   */
  static async isEnabled(): Promise<boolean> {
    try {
      const status = await this.getStatus();
      return status.mfa_enabled;
    } catch (error) {
      return false;
    }
  }

  /**
   * Helper: Format backup codes for display (add dashes every 4 characters)
   */
  static formatBackupCode(code: string): string {
    return code.match(/.{1,4}/g)?.join('-') || code;
  }

  /**
   * Helper: Download backup codes as text file
   */
  static downloadBackupCodes(codes: string[], filename: string = 'mfa-backup-codes.txt'): void {
    const content = [
      'MULTI-FACTOR AUTHENTICATION BACKUP CODES',
      '=========================================',
      '',
      'IMPORTANT: Store these codes securely!',
      '- Each code can only be used once',
      '- Use these codes if you lose access to your authenticator app',
      '- Do not share these codes with anyone',
      '',
      'Your Backup Codes:',
      '-------------------',
      ...codes.map((code, index) => `${index + 1}. ${this.formatBackupCode(code)}`),
      '',
      `Generated: ${new Date().toLocaleString()}`,
      '',
      'Keep these codes in a safe place!'
    ].join('\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Helper: Copy backup codes to clipboard
   */
  static async copyBackupCodesToClipboard(codes: string[]): Promise<void> {
    const text = codes.map((code, index) => `${index + 1}. ${this.formatBackupCode(code)}`).join('\n');
    
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }
}

export default MfaService;
